import { useRef, useState } from "react";
import { DirectUpload } from "@rails/activestorage";

import { useSubmitIssue } from "./api";
import { readApiErrorMessage } from "./errors";

// Phase 4a customer-portal FR-13. Issue Reporter modal — the
// customer fills subject + description + severity, picks up to 5
// photos. Photos go through `@rails/activestorage` DirectUpload to
// the default ActiveStorage service; the server receives signed
// blob IDs and persists them on the SupportTicket via
// `photos.attach(signed_ids)`.
//
// HEIC accepted (AC-10). The mailer-side admin viewer is responsible
// for rendering the JPEG variant via the existing image_processing
// gem.

const ACCEPT = "image/jpeg,image/png,image/heic,image/heif";
const MAX_FILES = 5;
const MAX_BYTES = 10 * 1024 * 1024;
const DIRECT_UPLOAD_URL = "/rails/active_storage/direct_uploads";

interface Props {
  orderNumber: string;
  onClose: () => void;
  onSubmitted?: () => void;
}

interface PhotoState {
  filename: string;
  status: "uploading" | "ready" | "error";
  signedId?: string;
  error?: string;
}

export function IssueReporterDialog({ orderNumber, onClose, onSubmitted }: Props): JSX.Element {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<"unsafe" | "damaged" | "other">("damaged");
  const [photos, setPhotos] = useState<PhotoState[]>([]);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submit = useSubmitIssue(orderNumber);

  function handleFiles(files: FileList | null): void {
    if (!files || files.length === 0) return;
    const remaining = MAX_FILES - photos.length;
    if (remaining <= 0) {
      setError(`Max ${MAX_FILES} photos.`);
      return;
    }
    const picked = Array.from(files).slice(0, remaining);
    for (const file of picked) {
      if (file.size > MAX_BYTES) {
        setError(`${file.name} exceeds 10 MB.`);
        continue;
      }
      uploadOne(file);
    }
  }

  function uploadOne(file: File): void {
    const placeholder: PhotoState = { filename: file.name, status: "uploading" };
    setPhotos((s) => [...s, placeholder]);
    const upload = new DirectUpload(file, DIRECT_UPLOAD_URL);
    upload.create((err, blob) => {
      setPhotos((s) =>
        s.map((p): PhotoState => {
          if (p.filename !== file.name || p.status !== "uploading") return p;
          if (err) return { ...p, status: "error", error: err.message };
          if (blob == null) return { ...p, status: "error", error: "no blob returned" };
          return { ...p, status: "ready", signedId: blob.signed_id };
        })
      );
    });
  }

  function removePhoto(filename: string): void {
    setPhotos((s) => s.filter((p) => p.filename !== filename));
  }

  function handleSubmit(): void {
    if (description.trim().length < 20) {
      setError("Describe the issue in at least 20 characters.");
      return;
    }
    if (subject.trim().length === 0) {
      setError("Subject is required.");
      return;
    }
    if (photos.some((p) => p.status === "uploading")) {
      setError("Wait for photos to finish uploading.");
      return;
    }
    if (photos.some((p) => p.status === "error")) {
      setError("One or more photos failed to upload. Remove them and try again, or pick different files.");
      return;
    }
    const signedIds = photos.filter((p) => p.status === "ready" && p.signedId).map((p) => p.signedId as string);
    setError(null);
    submit.mutate(
      { subject, description, severity, photo_signed_ids: signedIds },
      {
        onSuccess: () => {
          onSubmitted?.();
          onClose();
        },
        onError: (e: Error) => setError(readApiErrorMessage(e))
      }
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="issue-reporter-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      data-testid="issue-reporter-dialog"
    >
      <div className="w-full max-w-md rounded bg-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 id="issue-reporter-title" className="text-lg font-semibold">
            Report an issue
          </h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-zinc-500">
            ×
          </button>
        </div>

        <label className="block space-y-1">
          <span className="text-sm">Subject</span>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            className="min-h-[44px] w-full rounded border px-3 py-2"
            data-testid="issue-subject"
          />
        </label>

        <fieldset className="space-y-1">
          <legend className="text-sm">Severity</legend>
          {(["unsafe", "damaged", "other"] as const).map((s) => (
            <label key={s} className="flex items-center gap-2 min-h-[44px] text-sm">
              <input
                type="radio"
                name="severity"
                value={s}
                checked={severity === s}
                onChange={() => setSeverity(s)}
                data-testid={`severity-${s}`}
              />
              <span>{severityLabel(s)}</span>
            </label>
          ))}
        </fieldset>

        <label className="block space-y-1">
          <span className="text-sm">What happened? (min 20 characters)</span>
          <textarea
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full rounded border px-3 py-2"
            data-testid="issue-description"
          />
        </label>

        <div className="space-y-1">
          <span className="text-sm">Photos (up to {MAX_FILES}, ≤10 MB each)</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT}
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            data-testid="issue-photos"
          />
          {photos.length > 0 ? (
            <ul className="text-sm space-y-1">
              {photos.map((p) => (
                <li key={p.filename} className="flex items-center justify-between gap-2">
                  <span>
                    {p.filename}{" "}
                    <span className="text-xs text-zinc-500">
                      {p.status === "uploading" ? "uploading…" : p.status === "ready" ? "ready" : p.error}
                    </span>
                  </span>
                  <button
                    type="button"
                    onClick={() => removePhoto(p.filename)}
                    className="text-xs underline"
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {error ? (
          <div className="rounded border border-red-600 bg-red-50 p-2 text-sm" data-testid="issue-error">
            {error}
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded border px-3 py-2 text-sm">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submit.isPending}
            className="rounded bg-zinc-900 px-3 py-2 text-sm text-white disabled:opacity-40"
            data-testid="issue-submit"
          >
            {submit.isPending ? "Submitting…" : "Submit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function severityLabel(s: "unsafe" | "damaged" | "other"): string {
  switch (s) {
    case "unsafe":
      return "Equipment unsafe / malfunctioning";
    case "damaged":
      return "Equipment damaged";
    case "other":
      return "Other";
  }
}
